/**
 * Was ein Termin im Arbeitsplan mehr hat als ein Termin im Planer: wer ausführt
 * (Packzettel oder eigenes Team), welche Leistungen an diesem Tag gemacht werden,
 * und ob es Nacharbeit ist. Steht im Arbeitstag-Blatt (Tag ohne Datum) und im
 * Termin-Widget (Tag mit Datum) — dasselbe Stück an beiden Stellen.
 *
 * Gespeichert wird sofort beim Wählen; der Speichern-Knopf des Termins betrifft
 * nur Zeit, Ort und Personen.
 *
 * Etappe 7: Steht der Tag im Kalender, sind seine Leistungen die Checkliste des
 * Termins — dieselbe, die die Helfer in der Team-App abhaken, mit Namen. Was nach
 * dem Tag offen ist, wird mit einem Griff ein Nacharbeitstag ohne Datum.
 */
import { useMemo, useState } from "react";
import { fehlerText } from "@vimaana/ui";

import Combobox, { type ComboboxEintrag } from "../Combobox";
import { useArbeitstagAendern, useHaken, useNacharbeit } from "../../hooks/useArbeitsplan";
import { positionsNummer, tagName, type Arbeitsplan, type PlanTag } from "../../lib/arbeitsplan";

const wann = (iso: string) => (iso ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) : "");

export default function ArbeitstagTeil({ plan, tag }: { plan: Arbeitsplan; tag: PlanTag }) {
  const aendern = useArbeitstagAendern(plan.projekt_id);
  const haken = useHaken(plan.projekt_id);
  const nacharbeit = useNacharbeit(plan.projekt_id);
  const [meldung, setMeldung] = useState("");
  const zettel = plan.packzettel.find((z) => z.id === tag.packzettel_id);
  const positionen = plan.angebot?.positionen || [];
  const gewaehlt = tag.positionen.map((p) => p.anker);

  const eintraege = useMemo<ComboboxEintrag[]>(() => {
    const frei = positionen.filter((p) => !gewaehlt.includes(p.id));
    // Die Leistungen des gewählten Packzettels oben, dann die noch freien, dann der Rest.
    const rang = (p: (typeof frei)[number]) =>
      (zettel && zettel.positionen.includes(p.id) ? 0 : 2) + (p.tage.length ? 1 : 0);
    return [...frei].sort((a, b) => rang(a) - rang(b)).map((p) => ({
      id: p.id,
      label: `Pos. ${positionsNummer(plan, p.id)} · ${p.titel || "Leistung"}`,
      zusatz: [
        p.tage.length ? `schon an Tag ${p.tage.join(", ")}` : "noch frei",
        p.packzettel ? p.packzettel.nummer : "",
        p.menge ? `${p.menge.toLocaleString("de-DE")} ${p.einheit}` : "",
        p.materialien.length ? `mit ${p.materialien.map((m) => m.name).join(", ")}` : "",
      ].filter(Boolean).join(" · "),
    }));
  }, [positionen, gewaehlt, zettel, plan]);

  const setze = (patch: Parameters<typeof aendern.mutate>[0]["patch"]) => aendern.mutate({ id: tag.id, patch });
  const abhaken = tag.im_kalender;
  const offen = tag.positionen.filter((p) => !p.erledigt_am && !p.anderswo_erledigt);
  const laeuft = aendern.isPending || haken.isPending || nacharbeit.isPending;

  return (
    <div className="apl-teil">
      <label className="apl-feld">
        <span className="apl-cap">Wer führt aus</span>
        <select
          value={tag.packzettel_id}
          disabled={aendern.isPending}
          onChange={(e) => setze({ packzettel_id: e.target.value })}
        >
          <option value="">Eigenes Team</option>
          {plan.packzettel.map((z) => (
            <option key={z.id} value={z.id}>
              {z.nummer} · {z.helfer_name || z.empfaenger.split("\n")[0] || "noch niemand"}
            </option>
          ))}
        </select>
      </label>
      {zettel && !zettel.helfer_id && (
        <p className="apl-leise">Am Packzettel {zettel.nummer} ist noch kein Helfer eingetragen — oben unter „Aus dem Angebot“.</p>
      )}

      <div className="apl-feld">
        <span className="apl-cap">
          Leistungen an diesem Tag
          {abhaken && tag.positionen.length > 0 && ` · ${tag.positionen.length - offen.length} von ${tag.positionen.length} erledigt`}
        </span>
        {tag.positionen.length === 0 && <p className="apl-leise">Noch keine Leistung gewählt.</p>}
        {abhaken ? (
          <ul className="apl-check">
            {tag.positionen.map((p) => {
              const fertig = !!p.erledigt_am;
              const name = p.bekannt ? `Pos. ${positionsNummer(plan, p.anker)} · ${p.titel}` : `${p.anker} (nicht mehr im Angebot)`;
              return (
                <li key={p.anker} className={"apl-check-zeile" + (fertig ? " is-erledigt" : "") + (p.bekannt ? "" : " is-alt")}>
                  <label className="apl-check-haken">
                    <input type="checkbox" checked={fertig} disabled={laeuft}
                      onChange={(e) => haken.mutate({ tagId: tag.id, anker: p.anker, erledigt: e.target.checked })} />
                    <span className="apl-check-text">
                      <span>{name}</span>
                      {fertig ? <small>erledigt · {p.erledigt_von || "?"} · {wann(p.erledigt_am)}</small>
                        : p.anderswo_erledigt ? <small>an einem anderen Tag erledigt</small> : null}
                    </span>
                  </label>
                  {!fertig && (
                    <button type="button" className="apl-x" aria-label={`${p.titel || p.anker} entfernen`} disabled={laeuft}
                      onClick={() => setze({ positionen: gewaehlt.filter((a) => a !== p.anker) })}>×</button>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <ul className="apl-chips">
            {tag.positionen.map((p) => (
              <li key={p.anker} className={"apl-chip" + (p.bekannt ? "" : " is-alt")}>
                <span>
                  {p.bekannt ? `Pos. ${positionsNummer(plan, p.anker)} · ${p.titel}` : `${p.anker} (nicht mehr im Angebot)`}
                </span>
                <button
                  type="button"
                  aria-label={`${p.titel || p.anker} entfernen`}
                  disabled={aendern.isPending}
                  onClick={() => setze({ positionen: gewaehlt.filter((a) => a !== p.anker) })}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        {abhaken && offen.length > 0 && (
          <div className="apl-check-knoepfe">
            <button type="button" className="vg-btn" disabled={laeuft}
              onClick={() => haken.mutate({ tagId: tag.id, alle: true, erledigt: true })}>
              Alles erledigt
            </button>
            {tag.vorbei && (
              <button type="button" className="vg-btn vg-btn-primary" disabled={laeuft}
                title="Was hier nicht abgehakt ist, kommt an einen neuen Tag ohne Datum"
                onClick={() => nacharbeit.mutate(tag.id, {
                  onSuccess: (d) => {
                    const neu = d.plan.tage.find((t) => t.id === d.id);
                    setMeldung(`${neu ? tagName(neu) : "Nacharbeit"} angelegt — ${offen.length === 1 ? "1 Leistung" : `${offen.length} Leistungen`}, noch ohne Datum.`);
                  },
                })}>
                Offenes als Nacharbeit planen ({offen.length})
              </button>
            )}
          </div>
        )}
        {meldung && <p className="apl-meldung" role="status">{meldung}</p>}
        {positionen.length > 0 ? (
          <Combobox
            wert=""
            eintraege={eintraege}
            onWaehlen={(id) => { if (id) setze({ positionen: [...gewaehlt, id] }); }}
            platzhalter="Leistung hinzufügen — Nummer oder Stichwort"
            ariaLabel="Leistung hinzufügen"
            disabled={aendern.isPending}
          />
        ) : (
          <p className="apl-leise">Für dieses Projekt gibt es kein Angebot mit Positionen.</p>
        )}
      </div>

      <label className="apl-schalter">
        <input
          type="checkbox"
          checked={tag.nacharbeit}
          disabled={aendern.isPending}
          onChange={(e) => setze({ nacharbeit: e.target.checked })}
        />
        <span>Nacharbeit</span>
      </label>
      {(aendern.error || haken.error || nacharbeit.error) && (
        <p className="apl-fehler" role="alert">{fehlerText(aendern.error || haken.error || nacharbeit.error)}</p>
      )}
    </div>
  );
}
